/**
 * Weight presets (§8, §14 "still open").
 *
 * §14 records the tiebreaker philosophy — best-player-available versus
 * need-weighted — as genuinely undecided, and the §8 weights as "guesses until
 * tested against real drafts". That is a question about the user's taste, not
 * about the engine, so the answer is to make the positions easy to try rather
 * than to pick one and hide the choice.
 *
 * Each preset is a coherent stance somebody could actually defend, with a
 * sentence saying what it does and what it costs. The names are the stance,
 * not a quality ranking — "Balanced" is the default, not the best.
 *
 * Pure module — no I/O.
 */

import type { BaselineMethod, EngineWeights } from './types.ts';
import { DEFAULT_WEIGHTS } from './types.ts';

export interface WeightPreset {
  id: string;
  label: string;
  /** One sentence: what this does. */
  description: string;
  /** One sentence: what it gives up. Every stance costs something. */
  tradeoff: string;
  weights: EngineWeights;
  baseline?: BaselineMethod;
}

export const WEIGHT_PRESETS: WeightPreset[] = [
  {
    id: 'balanced',
    label: 'Balanced',
    description:
      'Value leads, with roster fit, positional scarcity and timing as real but secondary pulls. The shipped default.',
    tradeoff:
      'Will sometimes offer you a fifth running back in the tenth round if the value gap is large enough.',
    weights: { ...DEFAULT_WEIGHTS },
    baseline: 'BEER',
  },
  {
    id: 'follow-the-list',
    label: 'Just follow my list',
    description:
      'Ignores the engine entirely and returns your active ranking source in order. Exactly, not approximately.',
    tradeoff:
      'No value, scarcity, need, or timing input at all — this is a strikethrough board with a leaderboard attached.',
    weights: { vor: 0, rank: 1, scarcity: 0, need: 0, availability: 0, bye: 0, lookahead: 0 },
  },
  {
    id: 'pure-value',
    label: 'Best player available',
    description:
      'Almost entirely value over replacement. Takes the biggest edge on the board regardless of what your roster looks like.',
    tradeoff:
      'Happily hands you four running backs and no tight end. You are expected to correct it yourself.',
    weights: { vor: 0.8, rank: 0.2, scarcity: 0, need: 0, availability: 0, bye: 0, lookahead: 0 },
    baseline: 'BEER',
  },
  {
    id: 'roster-first',
    label: 'Fill my roster',
    description:
      'Weights positional runs and roster fit heavily. Steers away from positions you have already filled and towards the ones you have not.',
    tradeoff:
      'Will reach — it takes the best player at a position you need over a clearly better player at one you do not. Roster fit says nothing until a position actually fills, so early on this behaves like a scarcity-led board.',
    weights: { vor: 0.18, rank: 0.1, scarcity: 0.25, need: 0.3, availability: 0.03, bye: 0.02, lookahead: 0.12 },
    baseline: 'BEER',
  },
  {
    id: 'dont-reach',
    label: "Don't reach",
    description:
      'Leans hard on timing: strongly prefers players you would actually lose by waiting, over players who will be there next turn anyway.',
    tradeoff:
      'Only as good as the ADP behind it, which thins out badly after about pick 150 and in unusual formats.',
    weights: { vor: 0.28, rank: 0.1, scarcity: 0.09, need: 0.08, availability: 0.33, bye: 0.02, lookahead: 0.1 },
    baseline: 'BEER',
  },
];

export const DEFAULT_PRESET_ID = 'balanced';

/** Which preset the current weights correspond to, or null if hand-tuned. */
export function matchPreset(weights: EngineWeights): WeightPreset | null {
  return (
    WEIGHT_PRESETS.find((preset) =>
      (Object.keys(weights) as (keyof EngineWeights)[]).every(
        (key) => Math.abs(preset.weights[key] - weights[key]) < 0.001,
      ),
    ) ?? null
  );
}

export const BASELINE_METHODS: { id: BaselineMethod; label: string; description: string }[] = [
  {
    id: 'BEER',
    label: 'BEER (man-games)',
    description:
      'Estimates how many players at each position your league will actually burn through over a season, accounting for byes and injuries. Sits between the other two. The BeerSheets method, and the default.',
  },
  {
    id: 'VOLS',
    label: 'VOLS (last starter)',
    description:
      'Baseline is the last player at the position who would start somewhere in the league in week one. Shallowest, so it flatters scarce positions.',
  },
  {
    id: 'VORP',
    label: 'VORP (replacement)',
    description:
      'Baseline set a round deeper, on the theory that the waiver wire is not empty. Deepest, so it compresses the value of every position.',
  },
];
